/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useSubfolderLanguageAndLocale } from '@coursera-web/internationalization-lib';

import { Grid, Link, Typography, Typography2 } from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LazyHydrate from 'bundles/page/components/shared/LazyHydrate';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import getProductPageUrlFromHit from 'bundles/search-common/utils/getProductPageUrlFromHit';
import useEnableSubfolderLinksForXDP from 'bundles/unified-description-page-common/hooks/useEnableSubfolderLinksForXDP';

import _t from 'i18n!nls/seo-entity-page';

const styles = {
  entityPageFeaturedSnippet: css`
    margin: 3rem;
    margin-left: 0;
    padding: 0 18px;

    @media (max-width: 1450px) {
      padding: 0 1.25rem;
      margin-left: auto;
      margin-right: auto;
      max-width: 1110px;
    }

    @media (max-width: 991px) {
      padding: 0 1.25rem;
    }
  `,
  featuredSnippetHeadline: css`
    margin-bottom: 1.5rem;
  `,
  featuredSnippetItem: css`
    margin-bottom: 0.75rem;
  `,
  featuredSnippetList: css`
    list-style: none;
    padding-inline-start: 0;
  `,
};

export type Props = {
  searchQuery: string;
  isSEOEntityPage?: boolean;
  searchResults?: SearchProductHit[];
};

export const EntityPageFeaturedSnippet = ({ searchQuery, isSEOEntityPage, searchResults }: Props) => {
  const [shouldDelayHydration, setShouldDelayHydration] = useState(!!isSEOEntityPage);
  const subfolderLanguageAndLocale = useSubfolderLanguageAndLocale();
  const enableSubfolderLinksForXDP = useEnableSubfolderLinksForXDP();

  let top10Results: SearchProductHit[] = [];
  if (!isSEOEntityPage) {
    return null;
  }
  if (searchResults && Array.isArray(searchResults) && searchResults.length > 0) {
    top10Results = searchResults.slice(0, 10);
  }

  if (top10Results.length > 0 && shouldDelayHydration) {
    setShouldDelayHydration(false);
  }

  if (top10Results.length < 1 && !shouldDelayHydration) {
    return null;
  }

  return (
    <Grid
      container
      component={TrackedDiv}
      css={styles.entityPageFeaturedSnippet}
      trackingName="entity_featured_snippet"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
      atMostOnce
      data={{ searchQuery }}
      data-e2e="entity_featured_snippet"
    >
      <LazyHydrate shouldHydrate={!shouldDelayHydration}>
        <Grid item xs={12}>
          <Typography
            variant="h2semibold"
            component="h2"
            css={styles.featuredSnippetHeadline}
            data-unit="featured-snippet-title"
          >
            {top10Results.length === 1
              ? _t('In summary, here is our most popular #{searchQuery} course', { searchQuery })
              : _t('In summary, here are #{resultNum} of our most popular #{searchQuery} courses', {
                  searchQuery,
                  resultNum: top10Results.length,
                })}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <ul css={styles.featuredSnippetList}>
            {top10Results.map((result) => {
              const { name, translatedName, partners } = result;
              const productUrl = getProductPageUrlFromHit({
                hit: result,
                shouldUseSubfolderLinksForXDP: enableSubfolderLinksForXDP,
                subfolderLocaleCode: subfolderLanguageAndLocale,
              });
              return (
                <li data-unit="top-10-result" css={styles.featuredSnippetItem} key={`featured-snippet-wrapper-${name}`}>
                  <Link
                    variant="standard"
                    typographyVariant="bodyPrimary"
                    component={TrackedLink2}
                    trackingName="featured_snippet_link"
                    className="featured-snippet-link"
                    key={`featured-snippet-link-${name}`}
                    href={productUrl}
                    data={{
                      name,
                    }}
                    data-e2e="featured-snippet-link"
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.preventDefault();
                      window.location.assign(`${productUrl}?trk_location=query-summary-list-link`);
                    }}
                  >
                    {translatedName || name}
                  </Link>
                  :
                  <Typography2 component="span" variant="bodyPrimary">
                    &nbsp;{partners?.[0]}
                  </Typography2>
                </li>
              );
            })}
          </ul>
        </Grid>
      </LazyHydrate>
    </Grid>
  );
};

export default EntityPageFeaturedSnippet;
